'use client';

import {
  DICTATION_HOTKEY_DEFAULTS,
  getUserPlatform,
  useDictationHotkey,
  type DictationHotkeyPlatform,
} from '@/features/dictation/hooks/useDictationHotkey';
import { isMac } from '@/hooks/platform';
import {
  dictateOnboardingDevPlatformOverrideAtom,
  isDictateOnboardingDevOverridesEnabled,
} from '@heidi/dictate';
import { useAtomValue } from 'jotai';

import { useDictateOnboardingContext } from '../context/DictateOnboardingProvider';

/**
 * Platform for dictate onboarding previews. Respects dev-tools Mac/Windows override
 * in non-production builds; otherwise matches `@/hooks/platform`.
 */
export function useDictateOnboardingIsMac(): boolean {
  const platformOverride = useAtomValue(dictateOnboardingDevPlatformOverrideAtom);
  if (!isDictateOnboardingDevOverridesEnabled()) {
    return isMac;
  }
  if (platformOverride === 'mac') return true;
  if (platformOverride === 'windows') return false;
  return isMac;
}

/**
 * Hotkey chord for onboarding UI. When the keyboard preview OS differs from the
 * runtime (e.g. Mac dev previewing Windows layout), use that preview OS default
 * so the illustration highlights Right Ctrl on Windows, not Fn.
 */
export function useDictateOnboardingHotkey(): { hotkey: string[] } {
  const isMacPreview = useDictateOnboardingIsMac();
  const { variant, selectedHotkey, currentStep } = useDictateOnboardingContext();
  const { hotkey } = useDictationHotkey();
  const runtimePlatform = getUserPlatform();
  const previewPlatform: DictationHotkeyPlatform = isMacPreview ? 'mac' : 'windows';

  // Full onboarding starts before settings exist, so key-confirm validates the
  // platform default. Authenticated lite validates the user's configured key.
  if (currentStep === 'key-confirm' && variant !== 'lite') {
    return { hotkey: [...DICTATION_HOTKEY_DEFAULTS[previewPlatform]] };
  }

  if (selectedHotkey) {
    return { hotkey: selectedHotkey };
  }

  if (runtimePlatform !== previewPlatform) {
    return { hotkey: [...DICTATION_HOTKEY_DEFAULTS[previewPlatform]] };
  }
  return { hotkey };
}
